import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import comms from '../comms';
import TimeStepper from '../ui/TimeStepper';
import LocationPicker from './LocationPicker';
import { absMins, dayOffsetFrom, composeDateTimeUtcIso } from './RouteEditorSheet';

// Friendly copy for the server's SPLIT_* codes (driverScheduling.js SPLIT_SQL_ERRORS
// plus the validation-time codes it throws before ever hitting SQL).
const ERROR_COPY = {
  DRIVER_AUTH_REQUIRED: 'Your session expired — log out and back in, then try again.',
  DRIVER_ID_MISMATCH: 'Your session expired — log out and back in, then try again.',
  NOT_FUTURE_SERVICE_DATE: 'Only a future day’s route can be split.',
  ROUTE_NOT_FOUND: "This route can't be split right now — try reopening it.",
  ROUTE_NOT_AUTHORIZED: "This route isn't assigned to you.",
  ROUTE_HAS_TRIPS: 'This route already has trips on it — trips must be removed before it can be split.',
  INSUFFICIENT_SPLIT_RESERVES: 'No spare route template is available to split into. Ask your dispatcher to add one.',
  SPLIT_CONFLICT: 'This route changed since it was loaded — reopening it to get the latest version.',
  SPLIT_NOT_AVAILABLE: 'Splitting routes is not available on this fleet.',
};

function friendlyError(res) {
  const code = res?.code || res?.errorCode;
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  return res?.error || res?.message || "Couldn't split this route — try again.";
}

function SplitRouteDialog({
  open, onClose, route, driverId, online, onToast, onSplit,
  serviceDate, start, end, startLoc, endLoc,
}) {
  const [handoffLoc, setHandoffLoc] = useState({ id: null, name: '' });
  const [firstEnd, setFirstEnd] = useState('');
  const [secondStart, setSecondStart] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setHandoffLoc(endLoc?.id ? endLoc : { id: null, name: '' });
    // Default the split point to the midpoint of the shift — the driver adjusts
    // from there. Both fields start equal (no gap) since a gap is optional.
    const s = absMins(start, start);
    const e = absMins(start, end);
    if (s != null && e != null && e > s) {
      const midAbs = s + Math.round((e - s) / 2);
      const midHM = `${String(Math.floor((midAbs % 1440) / 60)).padStart(2, '0')}:${String(midAbs % 60).padStart(2, '0')}`;
      setFirstEnd(midHM);
      setSecondStart(midHM);
    } else {
      setFirstEnd('');
      setSecondStart('');
    }
  }, [open, start, end, endLoc]);

  if (!route) return null;

  const err = (msg) => setError(msg);

  const handleSplit = async () => {
    setError('');
    if (!online) { err('Connect to the internet to split this route.'); return; }
    if (!firstEnd || !secondStart) { err('Set both the first and second shift times.'); return; }
    if (!handoffLoc.id) { err('Choose a handoff location.'); return; }

    const startAbs       = absMins(start, start);
    const endAbs         = absMins(start, end);
    const firstEndAbs    = absMins(start, firstEnd);
    const secondStartAbs = absMins(start, secondStart);

    if (firstEndAbs <= startAbs) { err('The first shift must end after it starts.'); return; }
    if (secondStartAbs < firstEndAbs) { err('The second shift can’t start before the first one ends.'); return; }
    if (secondStartAbs >= endAbs) { err('The second shift must end after it starts.'); return; }

    setSubmitting(true);
    try {
      // Refetch the lock version right before submitting — the value on `route`
      // may be stale (loaded whenever the day was first opened), and a stale
      // value always fails as a retryable SPLIT_CONFLICT anyway.
      const detail = await comms.getRouteDetail({ driverId, routeId: route.RouteId });
      const sourceLockVersion = detail?.route?.lockVersion;
      if (typeof sourceLockVersion !== 'number') {
        err("Couldn't verify the latest route version — try again.");
        setSubmitting(false);
        return;
      }

      // Segment 1's start and segment 2's end must land EXACTLY on the route's own
      // StartDateTime/EndDateTime (@SourceStart/@SourceEnd in the server's validation) —
      // reconstructing them from the "HH:MM"-only display strings truncates whatever
      // seconds/milliseconds the real stored value carries, which fails the
      // @SourceStart/@SourceEnd window check even when the displayed times match.
      // Pass the just-fetched raw values straight through instead of rebuilding them.
      const segments = [
        {
          startDateTime: detail.route.startDateTime,
          endDateTime:   composeDateTimeUtcIso(serviceDate, firstEnd, dayOffsetFrom(start, firstEnd)),
          startVehicleLocationId: startLoc.id,
          endVehicleLocationId:   handoffLoc.id,
          active: true,
        },
        {
          startDateTime: composeDateTimeUtcIso(serviceDate, secondStart, dayOffsetFrom(start, secondStart)),
          endDateTime:   detail.route.endDateTime,
          startVehicleLocationId: handoffLoc.id,
          endVehicleLocationId:   endLoc.id,
          active: true,
        },
      ];

      const res = await comms.splitRoute({ driverId, routeId: route.RouteId, sourceLockVersion, segments });
      if (res?.status === true) {
        onToast('Route split into two.');
        onSplit();
        onClose();
      } else {
        err(friendlyError(res));
      }
    } catch (e) {
      err(e?.message || "Couldn't split this route — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 20, margin: 16, maxHeight: 'calc(100% - 32px)' } }}
      >
        <DialogTitle style={{ padding: '20px 20px 4px' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>
            Split into two routes
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#94A3B8', marginTop: 2 }}>
            {route.RouteName || 'Route'}
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: '16px 20px' }}>
          {error && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
              padding: '10px 14px', marginBottom: 16,
              fontSize: 13, color: '#991B1B', fontWeight: 500, lineHeight: 1.4,
            }}>
              {error}
            </div>
          )}

          <div style={{
            fontSize: 12, color: '#64748B', lineHeight: 1.5, marginBottom: 18,
            background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '10px 12px',
          }}>
            Splits this day only — your recurring weekly schedule doesn’t change.
            The gap between the two shifts is off-duty time.
          </div>

          <div style={{
            fontSize: 11, fontWeight: 700, color: '#94A3B8',
            textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10,
          }}>
            First shift
          </div>
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 8 }}>Starts at {start || '—'}</div>
          <TimeStepper label="Ends at" value={firstEnd} onChange={setFirstEnd} />

          <div style={{ height: 1, background: '#F1F5F9', margin: '20px 0' }} />

          <div style={{
            fontSize: 11, fontWeight: 700, color: '#94A3B8',
            textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10,
          }}>
            Handoff location
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', textAlign: 'left',
              background: '#F8FAFC', border: '1.5px solid #E2E8F0',
              borderRadius: 12, padding: '12px 14px', cursor: 'pointer',
            }}
          >
            <div>
              <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
                Where the first shift ends and the second begins
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: handoffLoc.name ? '#0F172A' : '#CBD5E1' }}>
                {handoffLoc.name || 'Choose location…'}
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          <div style={{ height: 1, background: '#F1F5F9', margin: '20px 0' }} />

          <div style={{
            fontSize: 11, fontWeight: 700, color: '#94A3B8',
            textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10,
          }}>
            Second shift
          </div>
          <TimeStepper label="Starts at" value={secondStart} onChange={setSecondStart} />
          <div style={{ fontSize: 13, color: '#64748B', marginTop: 8 }}>Ends at {end || '—'}</div>
        </DialogContent>

        <DialogActions style={{ padding: '8px 20px 20px', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              flex: 1, padding: '13px', borderRadius: 14, border: 'none',
              background: '#F1F5F9', color: '#64748B', fontSize: 14, fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSplit}
            disabled={submitting || !online}
            style={{
              flex: 1, padding: '13px', borderRadius: 14, border: 'none',
              background: submitting || !online ? '#93C5FD' : '#3B5BDB',
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: submitting || !online ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Splitting…' : !online ? 'Offline' : 'Split route'}
          </button>
        </DialogActions>
      </Dialog>

      <LocationPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        driverId={driverId}
        online={online}
        onToast={onToast}
        onSelect={(loc) => {
          setHandoffLoc({ id: loc.Id, name: loc.Name });
          setPickerOpen(false);
        }}
      />
    </>
  );
}

export default SplitRouteDialog;
